/**
 * prefill-policy-lookup — pure mappers for the PolicyPrefillSelector dropdown.
 *
 * Kept separate from the React component so the option-label/order logic is
 * unit-testable without a DOM (see policyPrefillOptions.test.ts).
 *
 * The server (workflows.ora.inforce_policy.qualify_inforce_homeowners) returns
 * `policy_options` in deterministic AD-4 order: expiration_date DESC, then
 * effective_date DESC, then policy_number ASC. `readPolicyOptions` /
 * `toPolicyOptions` preserve that order verbatim.
 *
 * The COMBOBOX, however, re-orders for display via
 * `sortPolicyOptionsForDisplay` — oldest-effective first, so the current
 * in-force term is at the top (operator preference 2026-07-28). That is a
 * presentation concern only; selection never resolves by position.
 */
import type { PolicyInForce } from "~/common/workflows/types/client";

export interface PolicyPrefillOption {
  /** The launch input value — the resolved policy number. */
  value: string;
  /** Human-readable label shown in the <option>. */
  label: string;
  /** The product/line discriminator used to fill `policy_type` on selection. */
  productGroup: string | null;
}

/**
 * Build the dropdown option label.
 * Format: `${policy_number} — ${product_group ?? policy_type} — exp ${expiration_date}`.
 * product_group is preferred; policy_type is the fallback when group is absent.
 */
export function policyOptionLabel(p: PolicyInForce): string {
  const number = p.policy_number ?? "(no number)";
  const group = p.product_group ?? p.policy_type ?? "";
  const exp = p.expiration_date ?? "";
  return `${number} — ${group} — exp ${exp}`;
}

/**
 * Map the server-returned `policy_options` to dropdown options, preserving the
 * returned (expiration-desc) order. Options without a policy_number are dropped
 * — they cannot become a launch input.
 */
export function toPolicyOptions(policies: readonly PolicyInForce[]): PolicyPrefillOption[] {
  const opts: PolicyPrefillOption[] = [];
  for (const p of policies) {
    if (!p.policy_number) continue;
    opts.push({
      value: p.policy_number,
      label: policyOptionLabel(p),
      productGroup: p.product_group ?? p.policy_type ?? null,
    });
  }
  return opts;
}

/**
 * Normalize one raw `policy_options` row to the canonical snake_case
 * PolicyInForce shape. The substrate's prefill emits rows with the TVF's
 * camelCase columns (`policyNumber`, `productGroup`, `effectiveDate`, …)
 * while the client_context path uses snake_case — accept BOTH so neither
 * source silently drops rows (the camelCase shape previously failed the
 * `"policy_number" in x` filter and the dropdown never rendered).
 */
export function normalizePolicyOption(raw: unknown): PolicyInForce | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const str = (...keys: string[]): string | null => {
    for (const k of keys) {
      const v = r[k];
      if (typeof v === "string" && v) return v;
    }
    return null;
  };
  const num = (...keys: string[]): number | null => {
    for (const k of keys) {
      const v = r[k];
      if (typeof v === "number") return v;
    }
    return null;
  };
  const policyNumber = str("policy_number", "policyNumber");
  if (!policyNumber) return null;
  return {
    policy_line_sk: num("policy_line_sk", "policyLineSk") ?? 0,
    policy_type: str("policy_type", "policyType") ?? "",
    expiration_date: str("expiration_date", "expirationDate") ?? "",
    expiring_premium: num("expiring_premium", "expiringPremium"),
    renewal_likelihood: (str("renewal_likelihood", "renewalLikelihood") ??
      "N/A") as PolicyInForce["renewal_likelihood"],
    status: (str("status") ?? "active") as PolicyInForce["status"],
    policy_number: policyNumber,
    effective_date: str("effective_date", "effectiveDate"),
    product_group: str("product_group", "productGroup"),
    carrier_id: str("carrier_id", "carrierId"),
    carrier_name: str("carrier_name", "carrierName"),
  };
}

/**
 * Narrow a freeform `prefill_values.policy_options` value to a PolicyInForce[].
 * `policy_options` rides as a freeform key in prefill_values (not in the input
 * schema), so callers must type-narrow before use. Rows are normalized from
 * either camelCase (substrate prefill) or snake_case (client_context) shape;
 * rows without a policy number are dropped (they can't become a launch input).
 */
export function readPolicyOptions(prefillValues: Record<string, unknown> | undefined): PolicyInForce[] {
  const raw = prefillValues?.policy_options;
  if (!Array.isArray(raw)) return [];
  return raw
    .map(normalizePolicyOption)
    .filter((x): x is PolicyInForce => x !== null);
}

/**
 * Stable per-TERM identity for one option row.
 *
 * A policy number is NOT an identity: one policy's current term and its future
 * renewal share a number and differ only by term (the substrate surfaces BOTH
 * as selectable options — see `inforce_policy.qualify_inforce_homeowners`, which
 * de-dupes on `(policyNumber, effectiveDate)`). Anything that resolves a row
 * from the number alone silently lands on whichever term sorts first, so the
 * form must carry THIS key alongside `policy_number`.
 *
 * `policy_line_sk` is the real per-line key, but `normalizePolicyOption`
 * defaults a missing one to 0 — so the term dates are folded in too and the key
 * stays distinct even when the substrate omits the sk.
 */
export function policyTermKey(p: PolicyInForce): string {
  const sk = typeof p.policy_line_sk === "number" && p.policy_line_sk > 0
    ? String(p.policy_line_sk)
    : "";
  return [sk, p.policy_number ?? "", p.effective_date ?? "", p.expiration_date ?? ""].join("|");
}

/**
 * Freeform launch-form key carrying the selected term's identity, alongside
 * `policy_number`. Rides `values` the same way `policy_options` /
 * `policy_number_resolution` do (stripped server-side by
 * `model_validate(extra='ignore')` — it is not a declared workflow input).
 */
export const POLICY_TERM_KEY_FIELD = "policy_term_key";

/** Read the committed term key out of the launch form values. */
export function readPolicyTermKey(
  values: Record<string, unknown> | undefined,
): string {
  const v = values?.[POLICY_TERM_KEY_FIELD];
  return typeof v === "string" ? v : "";
}

/**
 * Resolve the SELECTED option row.
 *
 * `termKey` (from `values.policy_term_key`) is authoritative — it names the term
 * the operator actually picked. When it is absent (the server's prefill fills
 * `policy_number` but has no channel to name a term) and several terms share the
 * number, this mirrors the substrate's `default_inforce_term` precedence:
 * the CURRENT in-force term wins (latest-expiring on a tie), else the
 * soonest-effective future renewal. Resolving by POSITION instead would be
 * fragile in both directions: the server payload arrives expiration-DESC (first
 * row = the future renewal) while the picker renders oldest-first, so "first
 * row" means opposite things depending on which list you hold. This function
 * therefore never looks at index — only at the term key and the term dates.
 */
export function resolveSelectedPolicy(
  policies: readonly PolicyInForce[],
  policyNumber: string | null | undefined,
  termKey?: string | null,
  today: Date = new Date(),
): PolicyInForce | null {
  if (termKey) {
    const byKey = policies.find((p) => policyTermKey(p) === termKey);
    // A stale key (options reloaded for another client) falls through to the
    // number below rather than silently selecting nothing.
    if (byKey) return byKey;
  }
  if (!policyNumber) return null;
  const squash = (s: string) => s.replace(/\s+/g, "").toUpperCase();
  const target = squash(policyNumber);
  const loose = policies.filter(
    (p) => p.policy_number
      && (p.policy_number === policyNumber || squash(p.policy_number) === target),
  );
  // Exact still beats space-insensitive; the term precedence below only breaks
  // ties WITHIN the winning set (i.e. several terms of the same policy).
  const exact = loose.filter((p) => p.policy_number === policyNumber);
  const candidates = exact.length ? exact : loose;
  if (candidates.length <= 1) return candidates[0] ?? null;

  const todayTs = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const current = candidates.filter((p) => {
    const start = parsePolicyDate(p.effective_date);
    const end = parsePolicyDate(p.expiration_date);
    return start !== null && end !== null && start <= todayTs && todayTs <= end;
  });
  if (current.length) {
    return current.reduce((best, p) =>
      (parsePolicyDate(p.expiration_date) ?? -Infinity)
        > (parsePolicyDate(best.expiration_date) ?? -Infinity)
        ? p
        : best,
    );
  }
  const future = candidates.filter((p) => {
    const start = parsePolicyDate(p.effective_date);
    return start !== null && todayTs < start;
  });
  if (future.length) {
    return future.reduce((best, p) =>
      (parsePolicyDate(p.effective_date) ?? Infinity)
        < (parsePolicyDate(best.effective_date) ?? Infinity)
        ? p
        : best,
    );
  }
  return candidates[0];
}

/**
 * Find the option row for a policy number (exact, then space-insensitive),
 * disambiguated by `termKey` when the number covers several terms.
 */
export function findPolicyOption(
  policies: readonly PolicyInForce[],
  policyNumber: string | null | undefined,
  termKey?: string | null,
  today?: Date,
): PolicyInForce | null {
  return resolveSelectedPolicy(policies, policyNumber, termKey, today);
}

/** Parse "YYYY-MM-DD" or "MM/DD/YYYY" to a UTC-midnight timestamp, or null. */
export function parsePolicyDate(value: string | null | undefined): number | null {
  if (!value) return null;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (iso) return Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim());
  if (us) return Date.UTC(Number(us[3]), Number(us[1]) - 1, Number(us[2]));
  return null;
}

const fmtUsWith = (ts: number, sep: string): string => {
  const d = new Date(ts);
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${mm}${sep}${dd}${sep}${d.getUTCFullYear()}`;
};

/** `MM/DD/YYYY` — the validation message mirrors the format the operator types. */
const fmtUs = (ts: number): string => fmtUsWith(ts, "/");

/**
 * Display format for a policy term date: `MM-DD-YYYY` (operator preference
 * 2026-07-28 — the substrate sends ISO `YYYY-MM-DD`, which reads as ambiguous
 * next to a US-format effective-date field).
 *
 * An unparseable value falls through trimmed rather than becoming "" — a weird
 * date should still be visible in the picker, not silently vanish.
 */
export function formatTermDate(value: string | null | undefined): string {
  if (!value) return "";
  const ts = parsePolicyDate(value);
  return ts === null ? value.trim() : fmtUsWith(ts, "-");
}

/**
 * Value for an HTML `type="date"` input. Prefill often emits `MM/DD/YYYY`;
 * browsers silently blank a date input that isn't `YYYY-MM-DD`.
 */
export function toIsoDateInput(value: string | null | undefined): string {
  if (!value) return "";
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const ts = parsePolicyDate(trimmed);
  if (ts === null) return "";
  const d = new Date(ts);
  const yyyy = String(d.getUTCFullYear());
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Display order for the picker: OLDEST-first by effective date, so the current
 * in-force term sits at the top and future renewals below it (operator
 * preference 2026-07-28).
 *
 * This deliberately DIVERGES from the server's AD-4 payload order (expiration
 * DESC — see `qualify_inforce_homeowners`), so it is applied at RENDER time
 * only: `readPolicyOptions` still returns the server order untouched, and
 * nothing resolves a selection by position (`resolveSelectedPolicy` keys off the
 * term key / term dates), so reordering the list cannot change what is selected.
 *
 * Ties break by expiration then policy number, making the list deterministic.
 * Rows whose effective date is absent/unparseable sort LAST rather than winning
 * a `null` comparison and jumping to the top.
 */
export function sortPolicyOptionsForDisplay(
  policies: readonly PolicyInForce[],
): PolicyInForce[] {
  return [...policies].sort((a, b) => {
    const ae = parsePolicyDate(a.effective_date);
    const be = parsePolicyDate(b.effective_date);
    if (ae === null && be !== null) return 1;
    if (be === null && ae !== null) return -1;
    if (ae !== null && be !== null && ae !== be) return ae - be;
    const ax = parsePolicyDate(a.expiration_date);
    const bx = parsePolicyDate(b.expiration_date);
    if (ax !== null && bx !== null && ax !== bx) return ax - bx;
    return (a.policy_number ?? "").localeCompare(b.policy_number ?? "");
  });
}

/**
 * Validate the form's effective_date against the SELECTED policy's term:
 * the date must be >= the policy's effective_date and <= its expiration_date.
 * Returns a user-facing error string, or null when valid / not applicable
 * (no policy selected among the options, empty date, or the policy row
 * carries no term dates to validate against).
 *
 * `termKey` picks WHICH term to validate against when a client's current term
 * and its future renewal share one policy number — without it a date correct
 * for the selected term is rejected against the other term's bounds.
 */
export function validateEffectiveDateAgainstPolicy(
  effectiveDate: unknown,
  policies: readonly PolicyInForce[],
  policyNumber: string | null | undefined,
  termKey?: string | null,
  today?: Date,
): string | null {
  const dateStr = typeof effectiveDate === "string" ? effectiveDate.trim() : "";
  if (!dateStr) return null;
  const policy = findPolicyOption(policies, policyNumber, termKey, today);
  if (!policy) return null;
  const start = parsePolicyDate(policy.effective_date);
  const end = parsePolicyDate(policy.expiration_date);
  if (start === null && end === null) return null;
  const entered = parsePolicyDate(dateStr);
  if (entered === null) return "Enter a valid date (MM/DD/YYYY).";
  if ((start !== null && entered < start) || (end !== null && entered > end)) {
    const startLabel = start !== null ? fmtUs(start) : "policy start";
    const endLabel = end !== null ? fmtUs(end) : "policy end";
    return `Effective date must fall within the policy term (${startLabel} – ${endLabel}).`;
  }
  return null;
}

/**
 * Policy-membership backstop (story 26752, ora surface): when the server
 * resolved this client's `policy_options`, a typed/selected policy number
 * must be one of them — the schema's free-text `policy_number` input
 * otherwise lets a typo, or another client's number, launch as-is.
 *
 * Extracted from `InstanceDetailDrawer.handleLaunch`'s inline check (the
 * SAME wording, so both hosts show one message) rather than left inline a
 * second time — a second copy is how the two launch producers drift, the
 * exact failure this module's other functions already guard against.
 *
 * No policy options resolved → nothing to validate against, free text stays
 * allowed (matches `validateEffectiveDateAgainstPolicy`'s own "not
 * applicable" contract).
 */
export function validatePolicyMembership(
  policies: readonly PolicyInForce[],
  policyNumber: string | null | undefined,
): string | null {
  const typed = typeof policyNumber === "string" ? policyNumber.trim() : "";
  if (policies.length === 0 || !typed) return null;
  if (policies.some((o) => o.policy_number === typed)) return null;
  return `"${typed}" isn't one of this client's policies — choose one from the Policy dropdown above.`;
}

/**
 * prefill-query-collapse (AC#2) — the `already_extracted` payload threaded into
 * `workflow_prefill` for the chosen in-force policy.
 *
 * The key names are EXACTLY the four declared on the workflows
 * `CarrierMortgageeUpdateInput` (`policy_number` / `carrier_id` / `carrier_name`
 * / `policy_type`). Any other name is silently stripped server-side by
 * `model_validate(extra='ignore')`, so prefill would fall back to the EDW query.
 * `policy_number` is the only key threaded for an uncorrelated pick (see below).
 */
export interface ThreadedPolicy {
  policy_number?: string;
  carrier_id?: string | null;
  carrier_name?: string | null;
  policy_type?: string | null;
  // Index signature so a ThreadedPolicy is assignable to the
  // `already_extracted: Record<string, unknown>` prefill payload param without a
  // cast at the call site (the four declared keys above are the threaded surface).
  [key: string]: unknown;
}

/**
 * Thread the chosen client's policy into the `already_extracted` prefill payload.
 *
 * Given the currently-selected client's in-force policies and the chosen policy
 * number, return the `already_extracted` object carrying the chosen row's
 * `policy_number` + `carrier_id` + `carrier_name` + `policy_type` — but ONLY when
 * the chosen number is a member of the supplied `policies` list (correlation).
 *
 * Correlation guarantee: the carrier/type are sourced from the row inside the
 * selected client's own policy set, so a stale or mismatched pick can never
 * thread another client's carrier. When the chosen number is NOT in the set
 * (or is absent), the function threads at most `{ policy_number }` — never a
 * foreign policy's carrier. When the number is empty/undefined, returns `{}`.
 *
 * Pure — no DOM, no network. `clientSk` is accepted to make the
 * client-correlation contract explicit at the call site (the caller passes the
 * SAME client's policy set), but the membership check is what enforces it.
 */
export function threadChosenPolicy(
  policies: readonly PolicyInForce[],
  _clientSk: number | null,
  chosenNumber: string | null | undefined,
): ThreadedPolicy {
  if (!chosenNumber) return {};
  const match = policies.find((p) => p.policy_number === chosenNumber);
  if (!match) {
    // Uncorrelated pick — thread only the number, never a foreign carrier.
    return { policy_number: chosenNumber };
  }
  return {
    policy_number: chosenNumber,
    carrier_id: match.carrier_id,
    carrier_name: match.carrier_name,
    policy_type: match.policy_type,
  };
}

// ---------------------------------------------------------------------------
// Clientless-prefill quarantine (ordering fix, 2026-08-03)
// ---------------------------------------------------------------------------

/**
 * Form fields that are DERIVED from the selected client's TVF policy
 * resolution: the policy pick drives type + carrier, and the number itself is
 * a forced selection from `policy_options`. A prefill that ran with NO client
 * selected only has the email's raw guesses for these — free-filling them
 * puts unvalidated text into fields the AM then has to fix after the client
 * pick swaps in the real dropdown (operator-reported, 2026-08-03).
 */
export const CLIENT_DERIVED_PREFILL_KEYS = [
  "policy_number",
  "policy_type",
  "carrier_name",
  "carrier_id",
] as const;

/**
 * Strip the client-derived keys from a CLIENTLESS prefill/extraction payload
 * before it reaches the form. Stage-A fields (change_type, effective_date,
 * mortgagees, old_mortgagee_name, …) pass through untouched — they don't
 * depend on the client. The raw extracted policy number still reaches the
 * SERVER as a disambiguation hint via `already_extracted` when prefill
 * re-runs after the client pick; this quarantine is display-only.
 *
 * v2 action-list contract: the producer moved `policy_number` (and the other
 * action-scoped keys) INSIDE `actions[0]`, and re-keyed its confidence entry to
 * the dotted address `actions.policy_number`. A top-level-only delete silently
 * stops quarantining those, so strip all three shapes: the flat key, the dotted
 * confidence address, and the key inside every action item. Callers pass either
 * a values payload (which carries `actions`) or a field-confidence map (which
 * carries dotted addresses) — both go through here.
 */
export function quarantineClientDerivedPrefill(
  values: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...values };
  for (const k of CLIENT_DERIVED_PREFILL_KEYS) {
    delete out[k];
    delete out[`actions.${k}`];
  }
  if (Array.isArray(out.actions)) {
    out.actions = out.actions.map((item) => {
      if (!item || typeof item !== "object" || Array.isArray(item)) return item;
      const action = { ...(item as Record<string, unknown>) };
      for (const k of CLIENT_DERIVED_PREFILL_KEYS) {
        delete action[k];
      }
      return action;
    });
  }
  return out;
}
